import React, { useState, useEffect } from 'react';
import { usersApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ColumnsMenu, useHiddenColumns } from '../components/common/ColumnsMenu';
import { DensityToggle, useDensity } from '../components/common/DensityToggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Key,
  Users as UsersIcon,
  Shield,
  Loader2,
  UserPlus,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  Network,
  ArrowUp,
  ArrowDown,
  CornerDownRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import UserFormContent from '../components/UserFormContent';

const roleColors = {
  admin: 'bg-red-100 text-red-700',
  delivery_manager: 'bg-purple-100 text-purple-700',
  team_lead: 'bg-blue-100 text-blue-700',
  recruiter: 'bg-green-100 text-green-700',
  client_spoc: 'bg-amber-100 text-amber-700',
};

const roleLabels = {
  admin: 'Admin',
  delivery_manager: 'Delivery Manager',
  team_lead: 'Team Lead',
  recruiter: 'Recruiter',
  client_spoc: 'Client SPOC',
};

// Available columns + which can be hidden. Name is required (anchor column)
// and Actions is always shown since the dropdown menu is core functionality.
const USERS_COLUMNS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'reports_to', label: 'Reports To' },
  { key: 'created', label: 'Created' },
  { key: 'actions', label: 'Actions', required: true },
];

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Persisted per-user column visibility — empty means "show everything".
  const [hiddenCols, setHiddenCols] = useHiddenColumns('users-table-cols');
  const [density, setDensity, densityClass] = useDensity('users-table-density');
  const isCol = (key) => !hiddenCols.has(key);

  // Managers/team-leads/admins eligible to be a "Reports to" target
  const managerOptions = React.useMemo(
    () =>
      users
        .filter((u) => ['admin', 'delivery_manager', 'team_lead'].includes(u.role))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [users]
  );
  // Reverse-lookup helpers for the "Reports to" chip + org-chart preview.
  // Built once per users-fetch and reused by every row + the modal.
  const userById = React.useMemo(() => {
    const map = new Map();
    for (const u of users) map.set(u.id, u);
    return map;
  }, [users]);
  const childrenByParent = React.useMemo(() => {
    const map = new Map();
    for (const u of users) {
      const p = u.reports_to;
      if (!p) continue;
      if (!map.has(p)) map.set(p, []);
      map.get(p).push(u);
    }
    // Stable ordering inside each parent bucket — name asc.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return map;
  }, [users]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  // Org-chart preview: when set, the OrgChartDialog renders for this user.
  // Stores the user object (not just id) so the dialog title stays stable even
  // if the user list refreshes mid-view.
  const [orgChartUser, setOrgChartUser] = useState(null);
  // Reassign-reports dialog: opened when a delete on a manager returns 409.
  // Carries the user being deleted, the reportee count, suggested managers,
  // and the currently-selected reassign target id.
  const [reassignState, setReassignState] = useState(null); // { user, reporteeCount, suggested, targetId, submitting }
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'recruiter',
    reports_to: '',
    also_recruits: undefined, // server applies role-based default
  });
  
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'recruiter',
      reports_to: '',
      also_recruits: undefined,
    });
    setShowPassword(false);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setFormLoading(true);
    try {
      await usersApi.create({
        ...formData,
        // backend expects omitted or null to skip; pass only if selected
        reports_to: formData.reports_to || null,
        // For non-TL/DM roles, drop the flag entirely so the backend uses its defaults
        also_recruits:
          formData.role === 'team_lead' || formData.role === 'delivery_manager'
            ? !!formData.also_recruits
            : null,
      });
      toast.success('User created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    
    setFormLoading(true);
    try {
      // Decide whether to include also_recruits in the PUT body — only when
      // the user actually changed it from the original value.
      const origAR = selectedUser.also_recruits;
      const newAR = formData.also_recruits;
      const arChanged = (origAR ?? null) !== (newAR ?? null);
      await usersApi.update(selectedUser.id, {
        name: formData.name,
        role: formData.role,
        email: formData.email !== selectedUser.email ? formData.email : undefined,
        // Only send reports_to when the user actually changed it — empty string
        // is the backend's sentinel for "clear".
        reports_to:
          (formData.reports_to ?? '') !== (selectedUser.reports_to ?? '')
            ? (formData.reports_to || '')
            : undefined,
        also_recruits: arChanged ? !!newAR : undefined,
      });
      toast.success('User updated successfully');
      setIsEditOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setFormLoading(true);
    try {
      await usersApi.resetPassword(selectedUser.id, newPassword);
      toast.success(`Password reset for ${selectedUser.email}`);
      setIsResetPasswordOpen(false);
      setNewPassword('');
      setShowNewPassword(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await usersApi.delete(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      // The backend refuses with 409 when the user has active direct reports.
      // We surface a dedicated dialog so the admin can pick a new manager,
      // rather than telling them to manually edit each reportee.
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 409 && detail?.code === 'reassign_reports_required') {
        setReassignState({
          user,
          reporteeCount: detail.reportee_count || 0,
          // Backend already excludes the user being deleted + their subtree to
          // prevent cycles, so we can render the list as-is.
          suggested: detail.suggested_managers || [],
          targetId: '',
          submitting: false,
        });
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  // Confirm step from the reassign dialog: bulk-moves reports to the chosen
  // manager and then completes the delete. Backend does both atomically.
  const handleConfirmReassignAndDelete = async () => {
    if (!reassignState?.targetId) return;
    setReassignState((s) => ({ ...s, submitting: true }));
    try {
      const res = await usersApi.delete(reassignState.user.id, reassignState.targetId);
      const moved = res?.data?.reportees_reassigned ?? reassignState.reporteeCount;
      toast.success(
        moved > 0
          ? `Deleted ${reassignState.user.name}. ${moved} report${moved === 1 ? '' : 's'} moved to new manager.`
          : `Deleted ${reassignState.user.name}.`
      );
      setReassignState(null);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reassign and delete'));
      setReassignState((s) => ({ ...s, submitting: false }));
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      reports_to: user.reports_to || '',
      also_recruits: user.also_recruits ?? undefined,
    });
    setIsEditOpen(true);
  };

  const openResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowNewPassword(false);
    setIsResetPasswordOpen(true);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators can access user management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UsersIcon className="w-7 h-7 text-teal-700" />
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage system users and their access</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsCreateOpen(true);
          }}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="user-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="delivery_manager">Delivery Manager</SelectItem>
                <SelectItem value="team_lead">Team Lead</SelectItem>
                <SelectItem value="recruiter">Recruiter</SelectItem>
                <SelectItem value="client_spoc">Client SPOC</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <DensityToggle density={density} onChange={setDensity} testid="users-density" />
            <ColumnsMenu
              columns={USERS_COLUMNS}
              hiddenKeys={hiddenCols}
              onChange={setHiddenCols}
              testid="users-columns-menu"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
          <CardDescription>All registered users in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <Table
            containerClassName="max-h-[calc(100vh-280px)] min-h-[360px]"
            className={densityClass}
            data-testid="users-table"
          >
            <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
              <TableRow>
                <TableHead>Name</TableHead>
                {isCol('email') && <TableHead>Email</TableHead>}
                {isCol('role') && <TableHead>Role</TableHead>}
                {isCol('reports_to') && <TableHead>Reports To</TableHead>}
                {isCol('created') && <TableHead>Created</TableHead>}
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                // Column count for the loading/empty cell colSpan — Name + Actions
                // are always shown, then add 1 for every optional column kept.
                const visibleCols = 2 + ['email', 'role', 'reports_to', 'created']
                  .filter((k) => isCol(k)).length;
                if (loading) {
                  return (
                    <TableRow>
                      <TableCell colSpan={visibleCols} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" />
                      </TableCell>
                    </TableRow>
                  );
                }
                if (filteredUsers.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={visibleCols} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  );
                }
                return null;
              })()}
              {!loading && filteredUsers.length > 0 && (
                <>
                  {filteredUsers.map((user) => {
                  const mgr = user.reports_to ? userById.get(user.reports_to) : null;
                  const reportCount = (childrenByParent.get(user.id) || []).length;
                  return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-teal-700">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                        {reportCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setOrgChartUser(user)}
                            data-testid={`user-reports-count-${user.id}`}
                            title={`${reportCount} direct report${reportCount === 1 ? '' : 's'} · view org chart`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-full px-1.5 py-0.5 transition"
                          >
                            <ArrowDown className="w-2.5 h-2.5" />
                            {reportCount}
                          </button>
                        )}
                      </div>
                    </TableCell>
                    {isCol('email') && (
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                    )}
                    {isCol('role') && (
                      <TableCell>
                        <Badge className={roleColors[user.role] || 'bg-slate-100'}>
                          {roleLabels[user.role] || user.role}
                        </Badge>
                      </TableCell>
                    )}
                    {isCol('reports_to') && (
                      <TableCell>
                        {mgr ? (
                          <button
                            type="button"
                            onClick={() => setOrgChartUser(user)}
                            data-testid={`user-reports-to-${user.id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-slate-700 hover:text-teal-700 hover:bg-slate-50 rounded-md px-2 py-1 transition group max-w-[180px]"
                            title={`Reports to ${mgr.name} (${roleLabels[mgr.role] || mgr.role}) · view org chart`}
                          >
                            <ArrowUp className="w-3 h-3 text-slate-400 group-hover:text-teal-600 flex-shrink-0" />
                            <span className="truncate">{mgr.name}</span>
                            <Badge
                              variant="outline"
                              className={`text-[9px] py-0 px-1 flex-shrink-0 ${roleColors[mgr.role] || ''} border-current/30`}
                            >
                              {(roleLabels[mgr.role] || mgr.role).split(' ').map(w => w[0]).join('').toUpperCase()}
                            </Badge>
                          </button>
                        ) : user.reports_to ? (
                          <span
                            className="text-[10px] text-rose-600 italic"
                            title="Manager record missing — possibly deactivated. Edit the user to reassign."
                            data-testid={`user-reports-to-orphan-${user.id}`}
                          >
                            unknown manager
                          </span>
                        ) : (
                          <span
                            className="text-xs text-slate-400 italic"
                            data-testid={`user-reports-to-none-${user.id}`}
                          >
                            —
                          </span>
                        )}
                      </TableCell>
                    )}
                    {isCol('created') && (
                      <TableCell className="text-slate-500">
                        {user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-menu-${user.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setOrgChartUser(user)}
                            data-testid={`user-orgchart-${user.id}`}
                          >
                            <Network className="w-4 h-4 mr-2" /> View hierarchy
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openEdit(user)}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetPassword(user)}>
                            <Key className="w-4 h-4 mr-2" /> Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(user)}
                            className="text-red-600"
                            disabled={user.id === currentUser?.id}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })}
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account</DialogDescription>
          </DialogHeader>
          <UserFormContent
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            submitText="Create User"
            formLoading={formLoading}
            isEdit={false}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            onCancel={() => setIsCreateOpen(false)}
            managerOptions={managerOptions}
          />
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details</DialogDescription>
          </DialogHeader>
          <UserFormContent
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            submitText="Update User"
            formLoading={formLoading}
            isEdit={true}
            onCancel={() => setIsEditOpen(false)}
            managerOptions={managerOptions}
            currentUserId={selectedUser?.id}
          />
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              The user will be notified that their password has been reset.
            </div>
            <div className="space-y-2">
              <Label>New Password *</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={formLoading || !newPassword}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-password-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Org-chart preview: opens when a user clicks the "Reports to" chip or
          the small report-count badge on a row. Shows the manager chain upward
          and the direct-report list downward — read-only, click any name to
          re-center the chart on them. */}
      <OrgChartDialog
        anchor={orgChartUser}
        userById={userById}
        childrenByParent={childrenByParent}
        currentUserId={currentUser?.id}
        onClose={() => setOrgChartUser(null)}
        onNavigate={(u) => setOrgChartUser(u)}
      />

      {/* Reassign-reports dialog: opens when delete returns 409 because the
          target user has active direct reports. Forces the admin to pick a
          new manager so no one is orphaned. */}
      <Dialog
        open={!!reassignState}
        onOpenChange={(o) => !o && !reassignState?.submitting && setReassignState(null)}
      >
        <DialogContent className="max-w-md" data-testid="reassign-reports-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-amber-600" />
              Reassign direct reports
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-slate-900">
                {reassignState?.user?.name}
              </span>{' '}
              has{' '}
              <span className="font-semibold text-amber-700">
                {reassignState?.reporteeCount} active direct report
                {reassignState?.reporteeCount === 1 ? '' : 's'}
              </span>
              . Pick a new manager for them — this happens atomically with the
              delete, so no one gets orphaned.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="reassign-target">New manager *</Label>
              <Select
                value={reassignState?.targetId || ''}
                onValueChange={(v) =>
                  setReassignState((s) => ({ ...s, targetId: v }))
                }
              >
                <SelectTrigger id="reassign-target" data-testid="reassign-target-select">
                  <SelectValue placeholder="Choose a manager…" />
                </SelectTrigger>
                <SelectContent>
                  {(reassignState?.suggested || []).map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`reassign-option-${m.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{m.name}</span>
                        <Badge
                          variant="outline"
                          className={`text-[9px] py-0 px-1 ${roleColors[m.role] || ''}`}
                        >
                          {roleLabels[m.role] || m.role}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                  {!reassignState?.suggested?.length && (
                    <div className="px-2 py-3 text-xs text-slate-500 italic">
                      No eligible managers in this org. Create one first.
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 leading-snug">
                Only admins, delivery managers, and team leads outside the
                deleted user's subtree are listed (to prevent cycles).
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReassignState(null)}
              disabled={reassignState?.submitting}
              data-testid="reassign-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReassignAndDelete}
              disabled={!reassignState?.targetId || reassignState?.submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reassign-confirm-btn"
            >
              {reassignState?.submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Reassign &amp; delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ====================== Org-chart preview dialog ======================
// Renders three vertical zones for the anchor user:
//   1. Manager chain (upward) — anchor.reports_to → parent's reports_to → …
//      Capped at 5 levels to keep the dialog short on pathological data.
//   2. The anchor (highlighted card)
//   3. Direct reports (downward) — children of the anchor.
// Every name in zones 1 & 3 is a button that re-anchors the chart, so an admin
// can walk the tree without closing the modal.
const OrgChartDialog = ({ anchor, userById, childrenByParent, currentUserId, onClose, onNavigate }) => {
  const open = !!anchor;

  // Walk up the manager chain. Cap at 5 to avoid runaway loops on bad data.
  const managerChain = React.useMemo(() => {
    if (!anchor) return [];
    const chain = [];
    let cur = anchor.reports_to ? userById.get(anchor.reports_to) : null;
    const seen = new Set([anchor.id]);
    while (cur && chain.length < 5 && !seen.has(cur.id)) {
      chain.push(cur);
      seen.add(cur.id);
      cur = cur.reports_to ? userById.get(cur.reports_to) : null;
    }
    // Top-most first so the chain reads top-to-bottom in the UI.
    return chain.reverse();
  }, [anchor, userById]);

  const directReports = React.useMemo(() => {
    if (!anchor) return [];
    return childrenByParent.get(anchor.id) || [];
  }, [anchor, childrenByParent]);

  const PersonCard = ({ u, variant = 'plain', testid }) => {
    const isYou = u.id === currentUserId;
    const subReports = (childrenByParent.get(u.id) || []).length;
    const tone = {
      plain: 'border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/40',
      anchor: 'border-teal-400 bg-teal-50 ring-2 ring-teal-100',
      manager: 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white',
    }[variant];
    return (
      <button
        type="button"
        onClick={() => variant !== 'anchor' && onNavigate(u)}
        disabled={variant === 'anchor'}
        data-testid={testid}
        className={`w-full text-left flex items-center gap-2 rounded-lg border px-2.5 py-2 transition ${tone} ${variant === 'anchor' ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-teal-700">
            {u.name?.charAt(0).toUpperCase() || '?'}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-900 truncate">{u.name}</span>
            {isYou && <Badge variant="outline" className="text-[9px] py-0 px-1">You</Badge>}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge className={`text-[9px] py-0 px-1 ${roleColors[u.role] || 'bg-slate-100'}`}>
              {roleLabels[u.role] || u.role}
            </Badge>
            {subReports > 0 && (
              <span className="text-[9px] text-slate-500" title={`${subReports} direct report${subReports === 1 ? '' : 's'}`}>
                · {subReports} report{subReports === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  if (!anchor) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="orgchart-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="w-5 h-5 text-teal-600" />
            Reporting hierarchy
          </DialogTitle>
          <DialogDescription>
            Read-only org-chart preview. Click any teammate to re-center the view on them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Manager chain */}
          {managerChain.length > 0 ? (
            <div data-testid="orgchart-managers">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                <ArrowUp className="w-3 h-3" /> Manager chain
              </div>
              <div className="space-y-1.5">
                {managerChain.map((m, idx) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <span
                      className="text-[10px] text-slate-400 font-mono w-5 text-right flex-shrink-0"
                      title={`Level ${managerChain.length - idx}`}
                    >
                      L{managerChain.length - idx}
                    </span>
                    <div className="flex-1 min-w-0">
                      <PersonCard u={m} variant="manager" testid={`orgchart-mgr-${m.id}`} />
                    </div>
                  </div>
                ))}
                <div className="flex justify-center text-slate-300">
                  <CornerDownRight className="w-4 h-4 -rotate-90" />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic px-1" data-testid="orgchart-no-manager">
              No manager assigned — this person sits at the top of their reporting line.
            </div>
          )}

          {/* Anchor */}
          <PersonCard u={anchor} variant="anchor" testid={`orgchart-anchor-${anchor.id}`} />

          {/* Direct reports */}
          {directReports.length > 0 ? (
            <div data-testid="orgchart-reports">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                <ArrowDown className="w-3 h-3" /> Direct reports ({directReports.length})
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {directReports.map((r) => (
                  <PersonCard
                    key={r.id}
                    u={r}
                    variant="plain"
                    testid={`orgchart-report-${r.id}`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic px-1" data-testid="orgchart-no-reports">
              No direct reports yet.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="orgchart-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Users;
